import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import pluralize from "pluralize";
import { useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CLUSTER_DETAILS_PAGE } from "helpers/constants/pages";
import {
  FINAL_REVIEW_UNLABELED,
  FLOW_PARAMETER,
  LABEL_CLASS_PARAMETER,
  REVIEW_PARAMETER,
  REVIEW_SORT_PARAMETER,
  REVIEW_TAB_PARAMETER,
} from "helpers/constants/miscellaneous";
import { formatNumberWithCommas } from "helpers/utility/formatters";
import { getTemporaryExportDataArray } from "redux/SingleDataset/selectors";
import { setTemporaryExportData } from "redux/SingleDataset/actions";
import {
  FINAL_REVIEW_PAGE_SIZE,
  FINAL_REVIEW_TABS,
  FinalReviewTabLabel,
  findSummaryLabel,
  getLabelTabCount,
  isFinalReviewTab,
  isSortOrder,
  DEFAULT_FINAL_REVIEW_SORT,
  useFinalReviewEdit,
  useFinalReviewSamples,
  useFinalReviewSamplesFrom,
  useFinalReviewSummary,
} from "hooks/LabelPropagation/useFinalReview";
import { useFlywheelStatus } from "hooks/LabelPropagation/useFlywheelStatus";
import { VLSegmented } from "views/uikit/VLSegmented/VLSegmented";
import { Option } from "views/uikit/FormFields/SelectList";
import { NoMatchFoundCardProps } from "views/components/NoMatchFoundCard";
import DataList from "../DataList";
import ClusterDetailsFilter from "../DataPageFilters/ClusterDetailsFilter";
import { ReviewPagination } from "../../StatsFooter/Paginator";
import {
  UNLABELED_NAME,
  createFinalReviewActionList,
  getSampleClassId,
  toFinalReviewPreview,
} from "./utils";
import SortOrderButton from "./SortOrderButton";
import ReviewModal from "views/components/LabelPropagation/ReviewModal";
import finalReviewStyles from "./style.module.scss";
import styles from "../DataList/style.module.scss";

interface LabelPropagationProps {
  onLabelClick: (value: string[]) => void;
  options?: Option[];
  displayNewFloatingPanel?: boolean;
  showLabelPropagation: boolean;
}

interface FinalReviewListProps {
  setReviewCounters: (text: ReactElement | null) => void;
  setReviewPagination: (pagination: ReviewPagination | null) => void;
  updateNavigationCluster: (data: any) => void;
  labelPropagation: LabelPropagationProps;
  reviewCounters: ReactElement | null;
}

/** Final review page: browse the propagated labels of a label, reassign or ignore images */
export default function FinalReviewList({
  setReviewCounters,
  setReviewPagination,
  updateNavigationCluster,
  labelPropagation,
  reviewCounters,
}: FinalReviewListProps) {
  const dispatch = useDispatch();
  const { datasetId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const flowId = searchParams.get(FLOW_PARAMETER);
  const labelId = searchParams.get(LABEL_CLASS_PARAMETER);
  const tabParam = searchParams.get(REVIEW_TAB_PARAMETER);
  const tab: FinalReviewTabLabel = isFinalReviewTab(tabParam)
    ? tabParam
    : "All";
  const sortParam = searchParams.get(REVIEW_SORT_PARAMETER);
  const sortOrder = isSortOrder(sortParam)
    ? sortParam
    : DEFAULT_FINAL_REVIEW_SORT;
  const isUnlabeled = labelId === FINAL_REVIEW_UNLABELED;

  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [labelId, tab, sortOrder]);

  const exportData = useSelector(getTemporaryExportDataArray);

  const { data: summary } = useFinalReviewSummary(datasetId, flowId);
  const {
    data: samplesPage,
    isLoading,
    isFetching,
  } = useFinalReviewSamples({
    datasetId,
    flowId,
    labelId,
    tab,
    sortOrder,
    page,
  });
  const { reassign, ignore } = useFinalReviewEdit(datasetId, flowId);

  const currentLabel = findSummaryLabel(summary, labelId);
  const labelName = isUnlabeled ? UNLABELED_NAME : currentLabel?.label;

  // open the first label when none is selected
  useEffect(() => {
    if (labelId || !summary?.labels.length) return;
    const firstLabelId = summary.labels[0].label_category_id;
    if (!firstLabelId) return;
    searchParams.set(LABEL_CLASS_PARAMETER, firstLabelId);
    setSearchParams(searchParams, { replace: true });
  }, [labelId, summary, searchParams, setSearchParams]);

  const setTab = useCallback(
    (value: FinalReviewTabLabel) => {
      searchParams.set(REVIEW_TAB_PARAMETER, value);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const toggleSortOrder = useCallback(() => {
    searchParams.set(
      REVIEW_SORT_PARAMETER,
      sortOrder === "asc" ? "desc" : "asc",
    );
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams, sortOrder]);

  const closeFinalReview = useCallback(() => {
    searchParams.delete(REVIEW_PARAMETER);
    searchParams.delete(LABEL_CLASS_PARAMETER);
    searchParams.delete(REVIEW_TAB_PARAMETER);
    searchParams.delete(REVIEW_SORT_PARAMETER);
    setSearchParams(searchParams);
    dispatch(setTemporaryExportData(null));
  }, [dispatch, searchParams, setSearchParams]);

  // a stale final review URL (e.g. the flow was applied or reset elsewhere)
  const flywheelStatus = useFlywheelStatus(datasetId);
  useEffect(() => {
    if (flywheelStatus && flywheelStatus !== "FINAL_REVIEW") {
      closeFinalReview();
    }
  }, [flywheelStatus, closeFinalReview]);

  // every other label, plus Unlabeled, as reassign targets
  const reassignOptions: Option[] = useMemo(() => {
    if (!summary) return [];
    const options: Option[] = summary.labels
      .filter((l) => !!l.label_category_id)
      .map((l) => ({ label: l.label, value: l.label_category_id! }));
    options.push({ label: UNLABELED_NAME, value: FINAL_REVIEW_UNLABELED });
    return options.filter((o) => o.value !== labelId);
  }, [summary, labelId]);

  const clearSelection = useCallback(
    () => dispatch(setTemporaryExportData(null)),
    [dispatch],
  );

  // id of the sample the review modal opens on
  const [reviewModalOpen, setReviewModalOpen] = useState<string>();

  // the modal browses the label from the current page on, loading the next pages near the end
  const {
    data: modalData,
    hasNextPage: modalHasMore,
    isFetchingNextPage: modalLoadingMore,
    fetchNextPage: fetchNextModalPage,
  } = useFinalReviewSamplesFrom({
    datasetId,
    flowId,
    labelId,
    tab,
    sortOrder,
    startPage: page,
    initialPage: samplesPage,
    enabled: !!reviewModalOpen,
  });
  const modalSamples = useMemo(
    () => modalData?.pages.flatMap((p) => p.samples) ?? [],
    [modalData],
  );
  const loadMoreModalSamples = useCallback(() => {
    fetchNextModalPage();
  }, [fetchNextModalPage]);

  // the actions get sample ids; undo needs the samples' state before the action
  const getSamples = useCallback(
    (sampleIds: string[]) => {
      const byId = new Map(
        [...(samplesPage?.samples ?? []), ...modalSamples].map((sample) => [
          sample.id,
          sample,
        ]),
      );
      return sampleIds.flatMap((id) => byId.get(id) ?? []);
    },
    [samplesPage, modalSamples],
  );

  const onReassign = useCallback(
    async (sampleIds: string[], targetLabelId: string) => {
      const targetName =
        reassignOptions.find((o) => o.value === targetLabelId)?.label ?? "";
      clearSelection();
      await reassign(getSamples(sampleIds), targetLabelId, targetName);
    },
    [reassign, getSamples, reassignOptions, clearSelection],
  );

  const onIgnore = useCallback(
    async (sampleIds: string[]) => {
      clearSelection();
      await ignore(getSamples(sampleIds));
    },
    [ignore, getSamples, clearSelection],
  );

  const previews = useMemo(
    () =>
      (samplesPage?.samples ?? []).map((sample) =>
        toFinalReviewPreview(
          sample,
          createFinalReviewActionList({
            actionType: "SINGLE",
            getSampleIds: () => [sample.id],
            reassignOptions,
            onReassign,
            // ignored images are already in Unlabeled
            onIgnore: isUnlabeled ? undefined : onIgnore,
          }),
          { onDetailsClick: () => setReviewModalOpen(sample.id) },
        ),
      ),
    [samplesPage, reassignOptions, onReassign, onIgnore, isUnlabeled],
  );

  // every label, plus Unlabeled, for the modal's Assign Label list
  const modalLabelsOptions: Option[] = useMemo(() => {
    if (!summary) return [];
    return [
      ...summary.labels
        .filter((l) => !!l.label_category_id)
        .map((l) => ({ label: l.label, value: l.label_category_id! })),
      { label: UNLABELED_NAME, value: FINAL_REVIEW_UNLABELED },
    ];
  }, [summary]);

  useEffect(() => {
    setReviewModalOpen(undefined);
  }, [labelId, tab, sortOrder, page]);

  // an edited image leaves the label: the modal moves on, and closes when no image is left
  useEffect(() => {
    if (modalData && !modalSamples.length) setReviewModalOpen(undefined);
  }, [modalData, modalSamples]);
  const modalItemIndex = modalSamples.findIndex(
    (s) => s.id === reviewModalOpen,
  );

  const bulkActionList = useMemo(
    () =>
      createFinalReviewActionList({
        actionType: "BULK",
        getSampleIds: () =>
          (exportData ?? [])
            .map((item: any) => item.media_id)
            .filter(Boolean) as string[],
        reassignOptions,
        onReassign,
        onIgnore: isUnlabeled ? undefined : onIgnore,
      }),
    [exportData, reassignOptions, onReassign, onIgnore, isUnlabeled],
  );

  // passed to DataList directly (not read back from redux) so a label switch never renders the
  // previous label's samples; redux is still updated for the components that read it from there
  const reviewCluster = useMemo(
    () =>
      labelId
        ? {
            id: labelId,
            cluster_id: labelId,
            label: { name: labelName ?? "" },
            title: "",
            previews,
          }
        : null,
    [labelId, labelName, previews],
  );

  useEffect(() => {
    if (reviewCluster) updateNavigationCluster(reviewCluster);
  }, [reviewCluster, updateNavigationCluster]);

  // an edit can empty the last page: step back to the previous one
  useEffect(() => {
    if (page > 1 && samplesPage && !samplesPage.samples.length && !isFetching) {
      setPage((p) => Math.max(1, p - 1));
    }
  }, [page, samplesPage, isFetching]);

  const totalCount = getLabelTabCount(currentLabel, tab, isUnlabeled);

  useEffect(() => {
    setReviewPagination(
      totalCount > FINAL_REVIEW_PAGE_SIZE
        ? {
            page,
            pageSize: FINAL_REVIEW_PAGE_SIZE,
            totalCount,
            onPageChange: setPage,
          }
        : null,
    );
  }, [page, totalCount, setReviewPagination]);

  useEffect(() => {
    setReviewCounters(
      labelName ? (
        <span>
          {formatNumberWithCommas(totalCount)} {pluralize("Image", totalCount)}
        </span>
      ) : null,
    );
  }, [labelName, totalCount, setReviewCounters]);

  useEffect(
    () => () => {
      setReviewPagination(null);
      setReviewCounters(null);
    },
    [setReviewPagination, setReviewCounters],
  );

  const emptyStateProps = useMemo<NoMatchFoundCardProps>(() => {
    if (tab !== "All") {
      return {
        onActionClick: () => setTab("All"),
        buttonText: "Show all images",
        text:
          tab === "Changed"
            ? "You did not change any image in this label yet."
            : "No images in this label were labeled by VL.",
      };
    }
    return {
      onActionClick: () => {},
      hideButton: true,
      text: isUnlabeled
        ? "You did not ignore any image yet."
        : "No images in this label.",
    };
  }, [tab, setTab, isUnlabeled]);

  const clusterLabel = labelName
    ? {
        text: labelName,
        onClick: closeFinalReview,
        tooltip: "Click to close the final review",
      }
    : undefined;

  const isContentLoading = !summary || isLoading;

  return (
    <>
      <DataList
        page={CLUSTER_DETAILS_PAGE}
        clusters={[]}
        navigationCluster={reviewCluster}
        navigationClusterTags={undefined}
        isContentLoading={isContentLoading}
        showMetadataSummary={true}
        labelPropagation={labelPropagation}
        isDataPage={false}
        rightPanelMode="labels"
        reviewCounters={reviewCounters}
        title={
          <div className={styles.title}>
            <span className={styles.titleText}>Final Review</span>
            {clusterLabel && <ClusterDetailsFilter label={clusterLabel} />}
            <div style={{ flexGrow: 1 }} />
            <div className={finalReviewStyles.controls}>
              <SortOrderButton
                sortOrder={sortOrder}
                onClick={toggleSortOrder}
              />
              <VLSegmented
                options={FINAL_REVIEW_TABS}
                value={tab}
                onChange={(value) => setTab(value as FinalReviewTabLabel)}
              />
            </div>
          </div>
        }
        isReview={true}
        bulkActionList={bulkActionList}
        emptyStateProps={emptyStateProps}
      />
      {reviewModalOpen && modalSamples.length > 0 && (
        <ReviewModal
          open
          selectedLabel={labelName}
          initialSelectedItem={Math.max(modalItemIndex, 0)}
          onClose={() => setReviewModalOpen(undefined)}
          labelsOptions={modalLabelsOptions}
          imagesList={modalSamples.map((sample) => ({
            id: sample.id,
            labelId: getSampleClassId(sample),
            src: sample.image_uri,
            title: sample.original_uri,
            opacity: sample.ignored ? 50 : 100,
          }))}
          onCheckBoxClick={async ({ labelId: targetId, sampleId }) => {
            const sample = modalSamples.find((s) => s.id === sampleId);
            if (!sample || getSampleClassId(sample) === targetId) return;
            await onReassign([sampleId], targetId);
          }}
          counters={{ total: totalCount, forReview: 0 }}
          pagination={{
            infinite: false,
            hasMore: modalHasMore,
            loadingMore: modalLoadingMore,
            onReachEnd: loadMoreModalSamples,
            // images from the current page on
            totalCount: Math.max(
              totalCount - (page - 1) * FINAL_REVIEW_PAGE_SIZE,
              modalSamples.length,
            ),
          }}
        />
      )}
    </>
  );
}
